import { useContext, useEffect, useMemo, useState } from 'react'
import { Box, Button, Chip, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, styled, tableCellClasses } from '@mui/material'
import { AddOutlined, AddTaskOutlined, ArrowDownwardOutlined, ArrowUpwardOutlined, ContentCopyOutlined, InfoOutlined } from '@mui/icons-material'
import { PageOuterContainer } from '../../../root-components/layouts/page-outer-container'
import { PageHeading } from '../../presentation-components/display-elements-components/page-heading'
import { LoadingPage } from '../loading-page'
import { IconName } from '../../../shared/interfaces/icon-picker.model'
import { UserNotificationsContext } from '../../../context/user-notifications/user-notifications'
import { ReportPeriodContext } from '../../../context/report-periods/report-periods'
import ReductionScenarioService from '../../../shared/services/reduction-scenario/reduction-scenario.service'
import TargetSettingService from '../../../shared/services/target-setting/target-setting.service'
import { IReductionScenario, IReductionScenarioProject } from '../../../shared/services/reduction-scenario/reduction-scenario.model'
import { ReductionScenarioDialog } from '../../dialogs/reduction/reduction-scenario-dialog'
import { ReductionScenarioProjectDialog } from '../../dialogs/reduction/reduction-scenario-project-dialog'
import { TableActionButtons } from '../../presentation-components/miscellaneous-components/table-action-buttons'
import { costPerTonnePerYear, formatCostPerTonne } from '../../../shared/utils/reduction-utils'

const formatTce = (kg: number): string =>
  new Intl.NumberFormat('en-GB', { maximumFractionDigits: 2 }).format(Number(kg) / 1000)

// Matches the app's standard table header (grey, rounded top corners) — see table-display.tsx.
const HeadCell = styled(TableCell)(({ theme }: any) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.lightGrey.light,
    fontWeight: 600,
    '&:first-of-type': { borderTopLeftRadius: '10px' },
    '&:last-of-type': { borderTopRightRadius: '10px' },
  },
}))

export const ReductionScenariosPage = () => {
  const userNotificationsContext = useContext(UserNotificationsContext)
  const reportPeriodContext = useContext(ReportPeriodContext)
  const reductionScenarioService = new ReductionScenarioService()
  const targetSettingService = new TargetSettingService()

  const [scenarios, setScenarios] = useState<IReductionScenario[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [selectableYears, setSelectableYears] = useState<number[]>([])

  const [scenarioDialogOpen, setScenarioDialogOpen] = useState(false)
  const [editingScenario, setEditingScenario] = useState<IReductionScenario | null>(null)

  const [projectDialogOpen, setProjectDialogOpen] = useState(false)
  const [projectScenarioId, setProjectScenarioId] = useState<string>('')
  const [editingProject, setEditingProject] = useState<IReductionScenarioProject | null>(null)

  const getScenarios = () => {
    reductionScenarioService.getReductionScenarios()
      .then((response) => {
        setScenarios(response)
        setIsLoading(false)
      })
      .catch((err) => {
        console.log(err)
        setScenarios([])
        setIsLoading(false)
      })
  }

  useEffect(() => {
    getScenarios()
    targetSettingService.getTargetSettings()
      .then((response) => {
        const row = Array.isArray(response) ? response[0] : response
        const base = row?.baseYear ? Number(row.baseYear) : new Date().getFullYear()
        const target = row?.targetYear ? Number(row.targetYear) : base + 30
        const years: number[] = []
        for (let y = base; y <= target; y++) years.push(y)
        setSelectableYears(years.length > 0 ? years : [new Date().getFullYear()])
      })
      .catch((err) => {
        console.log(err)
        // Back-date the fallback so it still covers a typical base year if settings fail to load.
        const start = new Date().getFullYear() - 5
        setSelectableYears(Array.from({ length: 41 }, (_, i) => start + i))
      })
  }, [])

  const openCreateScenario = () => {
    setEditingScenario(null)
    setScenarioDialogOpen(true)
  }
  const openEditScenario = (scenario: IReductionScenario) => {
    setEditingScenario(scenario)
    setScenarioDialogOpen(true)
  }
  const openCreateProject = (scenarioId: string) => {
    setProjectScenarioId(scenarioId)
    setEditingProject(null)
    setProjectDialogOpen(true)
  }
  const openEditProject = (scenarioId: string, project: IReductionScenarioProject) => {
    setProjectScenarioId(scenarioId)
    setEditingProject(project)
    setProjectDialogOpen(true)
  }

  const duplicateScenario = (scenario: IReductionScenario) => {
    reductionScenarioService.duplicateReductionScenario(scenario.id)
      .then(() => {
        userNotificationsContext.openSnackbar({ open: true, message: 'Scenario duplicated' })
        getScenarios()
      })
      .catch((err) => {
        console.log(err)
        userNotificationsContext.openSnackbar({ open: true, message: 'There was an error, please try again', backgroundColor: 'text.primary' })
      })
  }

  const deleteScenario = (scenario: IReductionScenario) => {
    userNotificationsContext.openDialog({
      active: true,
      title: 'Delete Scenario',
      message: `Are you sure you want to delete "${scenario.name}"? This action cannot be undone.`,
      buttonCopy: 'Delete',
      cancelButtonCopy: 'Cancel',
    }).then((confirmed) => {
      if (confirmed) {
        reductionScenarioService.deleteReductionScenario(scenario.id)
          .then(() => {
            userNotificationsContext.openSnackbar({ open: true, message: 'Scenario deleted' })
            getScenarios()
          })
          .catch((err) => {
            console.log(err)
            userNotificationsContext.openSnackbar({ open: true, message: 'There was an error, please try again', backgroundColor: 'text.primary' })
          })
      }
    })
  }

  const deleteProject = (scenarioId: string, project: IReductionScenarioProject) => {
    userNotificationsContext.openDialog({
      active: true,
      title: 'Remove Measure',
      message: `Are you sure you want to remove "${project.name}" from this scenario?`,
      buttonCopy: 'Remove',
      cancelButtonCopy: 'Cancel',
    }).then((confirmed) => {
      if (confirmed) {
        reductionScenarioService.deleteScenarioProject(scenarioId, project.id)
          .then(() => {
            userNotificationsContext.openSnackbar({ open: true, message: 'Measure removed' })
            getScenarios()
          })
          .catch((err) => {
            console.log(err)
            userNotificationsContext.openSnackbar({ open: true, message: 'There was an error, please try again', backgroundColor: 'text.primary' })
          })
      }
    })
  }

  // Commit a measure into the live reduction plan as a Not Started action (one-way; the scenario is unchanged).
  const addProjectToPlan = (scenarioId: string, project: IReductionScenarioProject) => {
    const reportPeriodId = reportPeriodContext.lastSelectedOpenReportId
    if (!reportPeriodId) {
      userNotificationsContext.openSnackbar({ open: true, message: 'You need an open reporting period to add to your plan.', backgroundColor: 'text.primary' })
      return
    }
    userNotificationsContext.openDialog({
      active: true,
      title: 'Add to reduction plan',
      message: `Add "${project.name}" to your reduction plan? It will be added as a Not Started action in your current reporting period. Your scenario won't change.`,
      buttonCopy: 'Add to plan',
      cancelButtonCopy: 'Cancel',
    }).then((confirmed) => {
      if (!confirmed) return
      reductionScenarioService.addScenarioProjectToPlan(scenarioId, project.id, reportPeriodId)
        .then(() => {
          userNotificationsContext.openSnackbar({ open: true, message: `"${project.name}" added to your reduction plan` })
        })
        .catch((err) => {
          console.log(err)
          const code = err?.response?.data?.code
          const message = code === 2
            ? 'This measure is already in your reduction plan.'
            : (err?.response?.data?.message || 'There was an error, please try again')
          userNotificationsContext.openSnackbar({ open: true, message, backgroundColor: 'text.primary' })
        })
    })
  }

  return (
    <PageOuterContainer>
      <PageHeading
        title='Reduction Scenarios'
        hasTabs={false}
        includeActionButton={true}
        actionButtonIcon={IconName.Add}
        actionButtonHandler={openCreateScenario}
        actionButtonText='New Scenario'
        actionButtonVariant='text'
      />
      <Paper
        variant='outlined'
        sx={{ backgroundColor: 'lightGrey.light', p: 2, mb: 2, display: 'flex', alignItems: 'flex-start', gap: 1.5 }}
      >
        <InfoOutlined fontSize='small' sx={{ color: 'text.secondary', mt: '2px', flexShrink: 0 }} />
        <Typography variant='body2' sx={{ color: 'text.secondary' }}>
          Scenarios are what-if models for comparing future options, separate from your committed reduction plan.
          Each scenario draws its own line on the reduction graph and does not affect your reported plan or coverage.
          When you've settled on a measure, use its ⋯ menu to add it to your reduction plan as an action. That's what makes it count.
        </Typography>
      </Paper>
      <LoadingPage pageReady={!isLoading}>
        {scenarios.length === 0 ? (
          <Box sx={{ p: 2 }}>
            <Typography color='textSecondary'>No scenarios yet. Create one to start modelling future measures.</Typography>
          </Box>
        ) : (
          <Stack spacing={2}>
            {scenarios.map((scenario) => (
              <ScenarioCard
                key={scenario.id}
                scenario={scenario}
                onAddProject={() => openCreateProject(scenario.id)}
                onEditScenario={() => openEditScenario(scenario)}
                onDuplicateScenario={() => duplicateScenario(scenario)}
                onDeleteScenario={() => deleteScenario(scenario)}
                onEditProject={(project) => openEditProject(scenario.id, project)}
                onDeleteProject={(project) => deleteProject(scenario.id, project)}
                onAddProjectToPlan={(project) => addProjectToPlan(scenario.id, project)}
              />
            ))}
          </Stack>
        )}
      </LoadingPage>

      <ReductionScenarioDialog
        open={scenarioDialogOpen}
        scenario={editingScenario}
        closeHandler={() => setScenarioDialogOpen(false)}
        successHandler={getScenarios}
      />
      <ReductionScenarioProjectDialog
        open={projectDialogOpen}
        scenarioId={projectScenarioId}
        project={editingProject}
        selectableYears={selectableYears}
        closeHandler={() => setProjectDialogOpen(false)}
        successHandler={getScenarios}
      />
    </PageOuterContainer>
  )
}

const ScenarioCard = ({
  scenario,
  onAddProject,
  onEditScenario,
  onDuplicateScenario,
  onDeleteScenario,
  onEditProject,
  onDeleteProject,
  onAddProjectToPlan,
}: {
  scenario: IReductionScenario
  onAddProject: () => void
  onEditScenario: () => void
  onDuplicateScenario: () => void
  onDeleteScenario: () => void
  onEditProject: (project: IReductionScenarioProject) => void
  onDeleteProject: (project: IReductionScenarioProject) => void
  onAddProjectToPlan: (project: IReductionScenarioProject) => void
}) => {
  const projects = useMemo(
    () => [...(scenario.projects ?? [])].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)),
    [scenario.projects]
  )

  return (
    <Paper variant='outlined' sx={{ p: 3 }}>
      <Stack direction='row' justifyContent='space-between' alignItems='flex-start' flexWrap='wrap' gap={1} sx={{ mb: 2 }}>
        <Box>
          <Stack direction='row' spacing={1} alignItems='center'>
            <Typography variant='subtitle1'><b>{scenario.name}</b></Typography>
            {scenario.annualGrowthPercentage != null && scenario.annualGrowthPercentage !== 0 && (
              <Chip label={`${scenario.annualGrowthPercentage}% growth`} size='small' variant='outlined' />
            )}
          </Stack>
          {scenario.description && (
            <Typography variant='body2' sx={{ color: 'text.secondary' }}>{scenario.description}</Typography>
          )}
        </Box>
        <Stack direction='row' spacing={0.5} alignItems='center'>
          <Button size='small' startIcon={<AddOutlined />} onClick={onAddProject} sx={{ textTransform: 'none' }}>Add Measure</Button>
          <TableActionButtons
            id={scenario.id}
            includeEdit
            includeDelete
            onEdit={onEditScenario}
            onDelete={onDeleteScenario}
            additionalMenuItems={[{ label: 'Duplicate', icon: <ContentCopyOutlined sx={{ mr: 1 }} />, onClick: onDuplicateScenario }]}
            preferViewThenAdditionalThenDelete
          />
        </Stack>
      </Stack>

      {projects.length === 0 ? (
        <Typography variant='body2' sx={{ color: 'text.secondary' }}>No measures yet. Add a measure to model its impact.</Typography>
      ) : (
        <Paper variant='outlined' sx={{ maxWidth: '100%', overflowX: 'auto' }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <HeadCell sx={{ width: 56 }}>Action</HeadCell>
                  <HeadCell>Measure</HeadCell>
                  <HeadCell>Scope</HeadCell>
                  <HeadCell sx={{ whiteSpace: 'nowrap' }}>Annual Impact (tCO₂e)</HeadCell>
                  <HeadCell>Start Year</HeadCell>
                  <HeadCell>Lifetime</HeadCell>
                  <HeadCell sx={{ whiteSpace: 'nowrap' }}>Cost per tCO₂e</HeadCell>
                  <HeadCell>Behaviour</HeadCell>
                </TableRow>
              </TableHead>
              <TableBody sx={{ '& tr:last-of-type td': { borderBottom: 0 } }}>
                {projects.map((project) => {
                  const isIncrease = Number(project.annualReduction) < 0
                  const costPerTonne = isIncrease ? null : costPerTonnePerYear({
                    capexGbp: project.capexGbp,
                    annualOpexDeltaGbp: project.annualOpexDeltaGbp,
                    annualReductionKg: Math.abs(Number(project.annualReduction)),
                    lifetimeYears: project.lifetimeYears,
                  })
                  const marketOnly = String(project.scope) === '2' && project.scope2Basis === 'market'
                  return (
                    <TableRow key={project.id} hover>
                      <TableCell sx={{ width: 56 }}>
                        <TableActionButtons
                          id={project.id}
                          includeEdit
                          includeDelete
                          onEdit={() => onEditProject(project)}
                          onDelete={() => onDeleteProject(project)}
                          // Increases are what-if modelling only; the plan is a list of reduction actions.
                          additionalMenuItems={isIncrease ? [] : [{ label: 'Add to plan', icon: <AddTaskOutlined sx={{ mr: 1 }} />, onClick: () => onAddProjectToPlan(project) }]}
                          preferViewThenAdditionalThenDelete
                        />
                      </TableCell>
                      <TableCell>{project.name}</TableCell>
                      <TableCell>{`Scope ${project.scope}`}</TableCell>
                      <TableCell sx={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                        <Stack direction='row' alignItems='center' spacing={0.5}>
                          {isIncrease
                            ? <ArrowUpwardOutlined sx={{ fontSize: '16px', color: 'error.main' }} />
                            : <ArrowDownwardOutlined sx={{ fontSize: '16px', color: 'success.main' }} />}
                          <span>{formatTce(Math.abs(Number(project.annualReduction)))}</span>
                        </Stack>
                      </TableCell>
                      <TableCell sx={{ fontVariantNumeric: 'tabular-nums' }}>{project.startYear}</TableCell>
                      <TableCell>{project.lifetimeYears != null ? `${project.lifetimeYears} yr` : 'Ongoing'}</TableCell>
                      <TableCell sx={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                        {costPerTonne != null ? formatCostPerTonne(costPerTonne).replace(' per tCO2e a year', '') : <Typography variant='body2' sx={{ color: 'text.secondary' }}>—</Typography>}
                      </TableCell>
                      <TableCell>
                        {project.isCumulative || project.isPermanent || marketOnly ? (
                          <Stack direction='row' spacing={0.5} flexWrap='wrap' useFlexGap>
                            {project.isCumulative && <Chip label='Stacks yearly' size='small' variant='outlined' />}
                            {project.isPermanent && <Chip label='Permanent' size='small' variant='outlined' />}
                            {marketOnly && <Chip label='Market-based only' size='small' variant='outlined' />}
                          </Stack>
                        ) : (
                          <Typography variant='body2' sx={{ color: 'text.secondary' }}>—</Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
    </Paper>
  )
}
